import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { APIError } from '../types';

// CSRF token storage
let csrfToken: string | null = null;

// Create axios instance with default config
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Helper to get tenant subdomain from auth storage
function getTenantSubdomain(): string | null {
  try {
    const authStorage = localStorage.getItem('b2b-auth-storage');
    if (authStorage) {
      const parsed = JSON.parse(authStorage);
      return parsed?.state?.tenant?.subdomain || null;
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

// Fetch CSRF token from server
export async function fetchCSRFToken(): Promise<string | null> {
  try {
    const response = await axios.get('/api/v1/csrf-token', {
      withCredentials: true,
    });
    csrfToken = response.data?.csrf_token || response.headers['x-csrf-token'];
    return csrfToken;
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
    return null;
  }
}

// Get current CSRF token
export async function getCSRFToken(): Promise<string | null> {
  if (!csrfToken) {
    return fetchCSRFToken();
  }
  return csrfToken;
}

// Clear CSRF token (call on logout)
export function clearCSRFToken(): void {
  csrfToken = null;
}

// Request interceptor
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add CSRF token for state-changing requests
    const method = config.method?.toUpperCase();
    if (csrfToken && config.headers && method && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }

    // Add tenant subdomain header
    const storedSubdomain = getTenantSubdomain();
    if (storedSubdomain && config.headers) {
      config.headers['X-Tenant-Subdomain'] = storedSubdomain;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<APIError>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 - try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        await axios.post('/api/v1/auth/refresh', {}, { withCredentials: true });
        return apiClient(originalRequest);
      } catch {
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }

    // Handle CSRF errors
    if (error.response?.status === 403 && error.response.data?.code?.startsWith('CSRF_')) {
      const newToken = await fetchCSRFToken();
      if (newToken && originalRequest.headers) {
        originalRequest.headers['X-CSRF-Token'] = newToken;
        return apiClient(originalRequest);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;

// Helper functions
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const apiError = error.response?.data as APIError;
    const raw = apiError?.error;
    // Some endpoints (e.g. rate limiter) return error as an object
    // { code, message, retry_after }. Extract the message in that case.
    if (raw && typeof raw === 'object') {
      const obj = raw as { message?: string; retry_after?: number };
      if (obj.retry_after && obj.message) {
        return `${obj.message} (Bitte ${obj.retry_after}s warten)`;
      }
      return obj.message || 'An unexpected error occurred';
    }
    return (typeof raw === 'string' ? raw : '') || error.message || 'An unexpected error occurred';
  }
  return 'An unexpected error occurred';
}

export function isUpgradeRequired(error: unknown): boolean {
  if (axios.isAxiosError(error)) {
    const apiError = error.response?.data as APIError;
    return apiError?.upgrade_required === true;
  }
  return false;
}
